import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import api from '../../utils/api';
import CheckoutAssetsTable from '../../components/checkout/CheckoutAssetsTable';
import CheckoutForm from '../../components/checkout/CheckoutForm';
import CheckoutSidePanel from '../../components/checkout/CheckoutSidePanel';
import CheckoutEmailSection from '../../components/checkout/CheckoutEmailSection';
import AddPersonModal from '../../components/modals/AddPersonModal';
import Loader from '../../components/common/Loader';
import { PERMISSIONS, canFeatureForRole } from '../../utils/permissions';
import { useRolePermissionMatrix } from '../../hooks/useRolePermissionMatrix';
import '../../styles/Forms.css';

const CheckoutFormPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const [showAddPersonModal, setShowAddPersonModal] = useState(false);
  const [selectedAssets, setSelectedAssets] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [allUsers, setAllUsers] = useState([]); // All users (employees + admins)
  const [sites, setSites] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [locations, setLocations] = useState([]);

  const [formData, setFormData] = useState({
    checkoutDate: new Date().toISOString().split('T')[0],
    checkoutTo: 'person', // 'person' or 'site'
    assignedTo: '',
    dueDate: '',
    site: '',
    location: '',
    department: '',
    notes: '',
    sendEmail: false,
    emailAddresses: '',
  });
  const [checkoutPhotos, setCheckoutPhotos] = useState([]);
  const [photosMandatory, setPhotosMandatory] = useState(false);

  const { rolePermissions, permissionsReady } = useRolePermissionMatrix();
  const normalizedUserRole = useMemo(() => {
    try {
      const u = JSON.parse(sessionStorage.getItem('user') || '{}');
      return String(u?.role || '').trim().toLowerCase();
    } catch (error) {
      return '';
    }
  }, []);
  const allowCheckout = useMemo(
    () => canFeatureForRole(normalizedUserRole, rolePermissions, [PERMISSIONS.CHECKOUT]),
    [normalizedUserRole, rolePermissions]
  );

  useEffect(() => {
    if (!permissionsReady) return;
    if (!allowCheckout) {
      toast.error('You do not have permission to check out assets');
      navigate('/admin/dashboard', { replace: true });
      return;
    }
    if (location.state && location.state.selectedAssets) {
      setSelectedAssets(location.state.selectedAssets);
    } else {
      toast.error('No assets selected');
      navigate('/admin/checkout');
      return;
    }

    fetchInitialData();
  }, [location.state, navigate, permissionsReady, allowCheckout]);

  // Clear photos when multiple assets are selected (photos only allowed for single asset)
  useEffect(() => {
    if (selectedAssets.length > 1 && checkoutPhotos.length > 0) {
      setCheckoutPhotos([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedAssets.length]);

  const fetchInitialData = async () => {
    try {
      setLoading(true);
      
      // Fetch settings to check if photos are mandatory
      try {
        const settingsRes = await api.getSettings();
        if (settingsRes.success && settingsRes.data) {
          setPhotosMandatory(settingsRes.data.checkoutPhotosMandatory || false);
        }
      } catch (error) {
        console.error('Error fetching settings:', error);
      }
      
      // Fetch all users (employees + admins) for checkout
      const allUsersRes = await api.getUsers({ isActive: 'true', limit: 10000 });
      if (allUsersRes.success) {
        const users = allUsersRes.data || [];
        setAllUsers(users);
        // Keep employees for backward compatibility
        setEmployees(users.filter(u => u.role === 'employee'));
      }

      // Fetch sites
      const sitesRes = await api.getSites({ isActive: 'true' });
      if (sitesRes.success) {
        setSites(sitesRes.data || []);
      }

      // Fetch departments
      const deptsRes = await api.getDepartments({ isActive: 'true' });
      if (deptsRes.success) {
        setDepartments(deptsRes.data || []);
      }

      // Fetch locations
      const locationsRes = await api.getLocations({ isActive: 'true' });
      if (locationsRes.success) {
        setLocations(locationsRes.data || []);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveAsset = (assetId) => {
    const updatedAssets = selectedAssets.filter(asset => (asset.id || asset._id) !== assetId);
    setSelectedAssets(updatedAssets);
    // Clear photos if multiple assets remain (photos only allowed for single asset)
    if (updatedAssets.length > 1 && checkoutPhotos.length > 0) {
      setCheckoutPhotos([]);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSiteChange = (siteId) => {
    setFormData(prev => ({ ...prev, site: siteId }));
  };

  const handleLocationChange = (locationName) => {
    setFormData(prev => ({ ...prev, location: locationName }));
  };

  const handleDepartmentChange = (deptId) => {
    setFormData(prev => ({ ...prev, department: deptId }));
  };

  const handleNotesChange = (value) => {
    setFormData(prev => ({ ...prev, notes: value }));
  };

  const handleAddPerson = async (personData) => {
    try {
      const response = await api.createUser({
        ...personData,
        role: 'employee',
        password: 'password123', // Default password, should be changed
      });
      
      if (response.success) {
        setEmployees([...employees, response.data]);
        setFormData(prev => ({ ...prev, assignedTo: response.data.id || response.data._id }));
        setShowAddPersonModal(false);
        toast.success('Person added successfully!');
      }
    } catch (error) {
      console.error('Error adding person:', error);
      toast.error('Error adding person: ' + (error.message || 'Unknown error'));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (selectedAssets.length === 0) {
      toast.error('Please select at least one asset');
      return;
    }

    if (formData.checkoutTo === 'person' && !formData.assignedTo) {
      toast.error('Please select an employee or add a new one');
      return;
    }

    // Validate photos if mandatory (only for single asset checkout)
    if (selectedAssets.length === 1 && photosMandatory && checkoutPhotos.length === 0) {
      toast.error('Please upload at least one photo for checkout');
      return;
    }

    try {
      setLoading(true);
      
      // Checkout each asset with skipEmail flag
      const checkoutPromises = selectedAssets.map(asset => {
        const assetId = asset.id || asset._id;
        
        // Build location object - only include fields that have values (changed by admin)
        // If no fields are changed, don't send location object at all to preserve original values
        const locationUpdate = {};
        if (formData.site) locationUpdate.site = formData.site;
        if (formData.department) locationUpdate.department = formData.department;
        if (formData.location) locationUpdate.locationDetail = formData.location;
        
        // Only pass photos for single asset checkout
        const photosToSend = selectedAssets.length === 1 ? checkoutPhotos : [];
        
        return api.checkoutAsset(assetId, {
          assignedTo: formData.checkoutTo === 'person' ? formData.assignedTo : undefined,
          siteId: formData.checkoutTo === 'site' ? formData.site : undefined,
          expectedReturnDate: formData.dueDate || undefined,
          location: Object.keys(locationUpdate).length > 0 ? locationUpdate : undefined,
          skipEmail: true, // Skip individual emails, will send consolidated email
        }, photosToSend); // Pass photos to API only for single asset
      });

      const checkoutResults = await Promise.all(checkoutPromises);
      
      // Send consolidated email after all checkouts complete
      try {
        const successfulAssets = checkoutResults
          .filter((result, index) => result.success)
          .map((result, index) => selectedAssets[checkoutResults.findIndex(r => r === result)]);
        
        if (successfulAssets.length > 0) {
          // Use bulk checkout endpoint if available, otherwise use existing sendCheckoutEmail
          const assetIds = successfulAssets.map(asset => asset.id || asset._id);
          await api.sendCheckoutEmail({
            emailAddresses: '', // Will be determined by backend based on assigned users
            assetIds,
            assignedTo: formData.checkoutTo === 'person' ? formData.assignedTo : undefined,
            dueDate: formData.dueDate || undefined,
            notes: formData.notes || undefined,
          });
        }
      } catch (error) {
        console.error('Error sending consolidated checkout email:', error);
        // Don't fail the checkout if email fails
      }

      // Create handover records - ONLY if assigned to a person
      if (formData.checkoutTo === 'person' && formData.assignedTo) {
        const handoverPromises = selectedAssets.map(async (asset) => {
          const assetId = asset.id || asset._id;
          try {
            const result = await api.createHandover({
              asset: assetId,
              assignedTo: formData.assignedTo,
              expectedReturnDate: formData.dueDate || undefined,
              notes: formData.notes || undefined,
            });
            if (!result.success) {
              console.error(`Failed to create handover for asset ${assetId}:`, result);
            }
            return result;
          } catch (error) {
            console.error(`Error creating handover for asset ${assetId}:`, error);
            // Don't throw - continue with other handovers
            return { success: false, error };
          }
        });

        const handoverResults = await Promise.all(handoverPromises);
      }

      // Send email if enabled
      if (formData.sendEmail && formData.emailAddresses) {
        try {
          const assetIds = selectedAssets.map(asset => asset.id || asset._id);
          await api.sendCheckoutEmail({
            emailAddresses: formData.emailAddresses,
            assetIds,
            assignedTo: formData.checkoutTo === 'person' ? formData.assignedTo : undefined,
            dueDate: formData.dueDate || undefined,
            notes: formData.notes || undefined,
          });
          toast.success('Checkout email notification sent');
        } catch (error) {
          console.error('Error sending checkout email:', error);
          toast.error('Error sending email notification: ' + (error.message || 'Unknown error'));
        }
      }

      toast.success('Assets checked out successfully!');
      navigate('/admin/assets');
    } catch (error) {
      console.error('Checkout error:', error);
      toast.error('Error checking out assets: ' + (error.message || 'Unknown error'));
    } finally {
      setLoading(false);
    }
  };

  if (!permissionsReady) {
    return <Loader />;
  }
  if (!allowCheckout) {
    return null;
  }

  if (loading && selectedAssets.length === 0) {
    return <Loader />;
  }

  if (selectedAssets.length === 0) {
    return (
      <div style={{ padding: '16px' }}>
        <div style={{
          background: 'white',
          padding: '60px',
          borderRadius: '8px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
          textAlign: 'center'
        }}>
          <div style={{ fontSize: '48px', marginBottom: '16px' }}>📦</div>
          <h3 style={{ margin: '0 0 8px 0', fontSize: '18px', fontWeight: 600, color: '#2d3748' }}>
            No Assets Selected
          </h3>
          <p style={{ margin: 0, color: '#718096', fontSize: '13px', marginBottom: '20px' }}>
            Please go back and select assets for checkout
          </p>
          <button
            onClick={() => navigate('/admin/checkout')}
            style={{
              padding: '10px 20px',
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontWeight: 600,
              fontSize: '13px'
            }}
          >
            Go to Checkout Page
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="checkout-form-page" style={{ padding: '16px' }}>
      {/* Assets Pending Section */}
      <div style={{
        background: 'white',
        padding: '16px',
        borderRadius: '8px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        marginBottom: '20px'
      }}>
        <h2 style={{ margin: '0 0 16px 0', fontSize: '16px', fontWeight: 600 }}>Assets Pending</h2>
        <CheckoutAssetsTable
          assets={selectedAssets}
          onRemove={handleRemoveAsset}
        />
      </div>

      {/* Form Section */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '20px' }}>
        {/* Left Panel - Form */}
        <div>
          <div style={{ 
            background: 'white', 
            padding: '16px', 
            borderRadius: '8px', 
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
            marginBottom: '16px'
          }}>
            <CheckoutForm
              formData={formData}
              employees={allUsers}
              sites={sites}
              onChange={handleInputChange}
              onAddPerson={() => setShowAddPersonModal(true)}
              photos={checkoutPhotos}
              onPhotosChange={setCheckoutPhotos}
              photosMandatory={photosMandatory}
              photosDisabled={selectedAssets.length > 1}
            />
          </div>
        </div>

        {/* Right Panel - Location Changes */}
        <div>
          <CheckoutSidePanel
            formData={formData}
            sites={sites}
            departments={departments}
            locations={locations}
            onSiteChange={handleSiteChange}
            onLocationChange={handleLocationChange}
            onDepartmentChange={handleDepartmentChange}
            onNotesChange={handleNotesChange}
          />
        </div>
      </div>

      {/* Email Section and Action Buttons */}
      <div style={{
        background: 'white',
        padding: '16px',
        borderRadius: '8px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        marginBottom: '20px'
      }}>
        <CheckoutEmailSection
          sendEmail={formData.sendEmail}
          emailAddresses={formData.emailAddresses}
          onChange={handleInputChange}
        />
      </div>

      {/* Action Buttons */}
      <div style={{ 
        display: 'flex', 
        gap: '10px', 
        justifyContent: 'flex-end',
        paddingTop: '16px',
        borderTop: '1px solid #e2e8f0'
      }}>
        <button
          type="button"
          onClick={() => navigate('/admin/checkout')}
          style={{
            padding: '10px 20px',
            background: '#e2e8f0',
            color: '#4a5568',
            border: 'none',
            borderRadius: '6px',
            cursor: 'pointer',
            fontWeight: 600,
            fontSize: '13px'
          }}
        >
          Cancel
        </button>
        <button
          onClick={handleSubmit}
          disabled={loading || selectedAssets.length === 0}
          style={{
            padding: '10px 20px',
            background: selectedAssets.length === 0 ? '#cbd5e0' : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: 'white',
            border: 'none',
            borderRadius: '6px',
            cursor: selectedAssets.length === 0 ? 'not-allowed' : 'pointer',
            fontWeight: 600,
            fontSize: '13px',
            opacity: loading ? 0.6 : 1
          }}
        >
          {loading ? 'Processing...' : 'Check-Out'}
        </button>
      </div>

      {/* Add Person Modal */}
      <AddPersonModal
        isOpen={showAddPersonModal}
        onClose={() => setShowAddPersonModal(false)}
        onAdd={handleAddPerson}
        sites={sites}
        departments={departments}
      />
    </div>
  );
};

export default CheckoutFormPage;

